import { Link, Route, Routes } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { participantsApi } from '../api/participants';
import { useAuth } from '../auth/AuthContext';
import { ParticipantEditor, ParticipantSummary } from '../components/ParticipantEditor';
import { ParticipantHistoryPanel } from '../components/ParticipantHistory';
import { ProblemAlert, StatusBadge } from '../components/ui';
import { InstallationDetailPage, ModelDetailPage, NewInstallationPage, NewModelPage, NewUnitPage, RegisterPage, UnitDetailPage } from '../components/DevicePages';
import { IncidentDetailPage, IncidentsPage, NewIncidentPage } from '../components/IncidentPages';
import { RecallDetailPage, RecallsPage } from '../components/RecallPages';
import { CaseDetailPage, CasesPage } from '../components/CasePages';
import { ChangePasswordForm, UsersPanel } from '../components/UsersPage';
import { HelpPage } from '../components/HelpPage';

const devices = { mode: 'participant' as const, basePath: '/participant/devices' };
const incidents = { mode: 'participant' as const, basePath: '/participant/incidents', devicesPath: '/participant/devices' };
const recalls = { mode: 'participant' as const, basePath: '/participant/recalls', devicesPath: '/participant/devices', incidentsPath: '/participant/incidents' };

export function ParticipantWorkbench() {
  return (
    <Routes>
      <Route index element={<ParticipantHome />} />
      <Route path="organisation" element={<OrganisationPage />} />
      <Route path="devices" element={<RegisterPage {...devices} />} />
      <Route path="devices/models/new" element={<NewModelPage {...devices} />} />
      <Route path="devices/models/:id" element={<ModelDetailPage {...devices} />} />
      <Route path="devices/units/new" element={<NewUnitPage {...devices} />} />
      <Route path="devices/units/:id" element={<UnitDetailPage {...devices} />} />
      <Route path="devices/installations/new" element={<NewInstallationPage {...devices} />} />
      <Route path="devices/installations/:id" element={<InstallationDetailPage {...devices} />} />
      <Route path="incidents" element={<IncidentsPage {...incidents} />} />
      <Route path="incidents/new" element={<NewIncidentPage {...incidents} />} />
      <Route path="incidents/:id" element={<IncidentDetailPage {...incidents} />} />
      <Route path="recalls" element={<RecallsPage {...recalls} />} />
      <Route path="compliance" element={<CasesPage mode="participant" basePath="/participant/compliance" />} />
      <Route path="compliance/:id" element={<CaseDetailPage mode="participant" basePath="/participant/compliance" />} />
      <Route path="recalls/:id" element={<RecallDetailPage {...recalls} />} />
      <Route path="users" element={<UsersPage />} />
      <Route path="account" element={<AccountPage />} />
      <Route path="help" element={<HelpPage />} />
      <Route path="*" element={<Placeholder />} />
    </Routes>
  );
}

function ParticipantHome() {
  const { user } = useAuth();
  const me = useQuery({ queryKey: ['participants', 'me'], queryFn: participantsApi.me });
  const isAdmin = user?.roles.includes('participant_admin') ?? false;
  return (
    <>
      <h1>Participant workbench</h1>
      <p>
        Signed in as {user?.displayName} for{' '}
        {me.data ? (
          <>
            <strong>{me.data.legalName}</strong> <StatusBadge status={me.data.status} />
          </>
        ) : (
          <code>{user?.participantId}</code>
        )}
        .
      </p>
      <div className="cards">
        <section className="card">
          <h2>Organisation</h2>
          <p>View and maintain your organisation's contact details. Every change is versioned with your reason.</p>
          <Link to="/participant/organisation">Organisation details</Link>
        </section>
        <section className="card">
          <h2>My devices</h2>
          <p>Register models, units and installations for your organisation. You can only ever see your own participant's records; rejected submissions show their reasons.</p>
          <Link to="/participant/devices">My devices</Link>
        </section>
        <section className="card">
          <h2>Incidents & recalls</h2>
          <p>Report faults against units you own or installed, or models you manufacture, and follow CER's response. Recalls list the units you are responsible for remediating.</p>
          <p>
            <Link to="/participant/incidents">Incidents</Link> · <Link to="/participant/incidents/new">Report an incident</Link> · <Link to="/participant/recalls">Recalls</Link> · <Link to="/participant/compliance">Compliance</Link>
          </p>
        </section>
        <section className="card">
          <h2>Users & account</h2>
          <p>{isAdmin ? 'Invite colleagues and assign administrator or user roles within your organisation. ' : ''}Change your own password.</p>
          <p>
            {isAdmin && (
              <>
                <Link to="/participant/users">Users</Link> ·{' '}
              </>
            )}
            <Link to="/participant/account">My account</Link>
          </p>
        </section>
      </div>
    </>
  );
}

function OrganisationPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const me = useQuery({ queryKey: ['participants', 'me'], queryFn: participantsApi.me });
  const isAdmin = user?.roles.includes('participant_admin') ?? false;

  if (me.isLoading) return <p className="muted">Loading…</p>;
  if (me.error || !me.data) return <ProblemAlert error={me.error ?? new Error('Not found')} />;
  const p = me.data;

  return (
    <>
      <p className="crumbs">
        <Link to="/participant">Overview</Link> / Organisation
      </p>
      <div className="row">
        <h1>{p.legalName}</h1>
        <StatusBadge status={p.status} />
      </div>
      <ParticipantSummary participant={p} />
      {isAdmin ? (
        <ParticipantEditor
          key={p.version}
          participant={p}
          mode="self"
          onSave={(patch) => participantsApi.updateMe({ details: patch.details, changeReason: patch.changeReason })}
          onSaved={(updated) => qc.setQueryData(['participants', 'me'], updated)}
        />
      ) : (
        <p className="muted">Only your participant administrator can change these details. Legal name, ABN and type are changed by CER.</p>
      )}
      <ParticipantHistoryPanel queryKey={['participants', 'me', 'history', p.version]} load={participantsApi.myHistory} />
    </>
  );
}

function UsersPage() {
  const { user } = useAuth();
  const isAdmin = user?.roles.includes('participant_admin') ?? false;
  return (
    <>
      <p className="crumbs">
        <Link to="/participant">Overview</Link> / Users
      </p>
      <h1>Users</h1>
      {isAdmin ? <UsersPanel participantId="me" canManage /> : <p className="muted">Only your participant administrator can view and manage users. Ask them if you need a colleague added or your role changed.</p>}
    </>
  );
}

function AccountPage() {
  const { user } = useAuth();
  return (
    <>
      <p className="crumbs">
        <Link to="/participant">Overview</Link> / My account
      </p>
      <h1>My account</h1>
      <p>
        Signed in as <strong>{user?.displayName}</strong> (<code>{user?.username}</code>), roles: {user?.roles.join(', ')}.
      </p>
      <ChangePasswordForm />
    </>
  );
}

function Placeholder() {
  return (
    <>
      <h1>Coming in a later phase</h1>
      <p className="muted">
        This area is planned for a later delivery phase. <Link to="/participant">Back to overview</Link>.
      </p>
    </>
  );
}
